/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import util from 'util';
import { LinkedError } from './linked-error';
let logger = undefined;
/**
 * @returns If it's called from a Node project, pino logger. Otherwise, browser console.
 */
async function getLogger() {
    if (logger) {
        return logger;
    }
    try {
        const loggingModule = await import('../logging');
        logger = loggingModule.getLogger({ file: __filename });
    }
    catch (err) {
        logger = console;
    }
    return logger;
}
// URLs can carry secrets in the query string (tokens, signatures, credentials, api keys, passwords), redact them before logging
const SENSITIVE_QUERY_PARAM_PATTERN = /([?&][^=&]*(?:token|secret|signature|credential|key|password)[^=&]*=)[^&\s"']+/gi;
function redactSensitiveQueryParams(url) {
    return url.replace(SENSITIVE_QUERY_PARAM_PATTERN, '$1<omitted>');
}
/**
 * Removes circular references (especially needed in Jest tests: https://github.com/jestjs/jest/issues/10577) and unnecessary fields
 */
function cleanError(error) {
    // axios requests can log the request headers and data:
    // as those can contain sensitive (or very large) data, we omit them
    if ('config' in error) {
        const cleanedConfig = { ...error.config };
        if ('data' in cleanedConfig) {
            cleanedConfig.data = '<omitted>';
        }
        if ('headers' in cleanedConfig) {
            cleanedConfig.headers = '<omitted>';
        }
        error.config = cleanedConfig;
        // omit any token/secret/credential-related values
        if ('params' in cleanedConfig && typeof cleanedConfig.params === 'object') {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const cleanedParams = { ...cleanedConfig.params };
            for (const key in cleanedParams) {
                if (/token|secret|signature|credential|key|password/i.test(key)) {
                    cleanedParams[key] = '<omitted>'; // sensitive values omitted
                }
            }
            cleanedConfig.params = cleanedParams;
        }
        // the url can itself carry secrets in its query string (e.g. a pre-signed URL)
        if ('url' in cleanedConfig && typeof cleanedConfig.url === 'string') {
            cleanedConfig.url = redactSensitiveQueryParams(cleanedConfig.url);
        }
        // keep only white-listed fields from config
        const whitelistConfigFields = ['method', 'url', 'params'];
        const finalConfig = {};
        whitelistConfigFields.forEach((field) => {
            if (field in cleanedConfig) {
                finalConfig[field] = cleanedConfig[field];
            }
        });
        error.config = finalConfig;
    }
    if ('request' in error) {
        const { _options, _ended, _ending, _redirectCount, _currentUrl } = error.request;
        if (_options && 'nativeProtocols' in _options) {
            _options.nativeProtocols = undefined; // useless info
        }
        if (_options && 'headers' in _options) {
            const SENSITIVE_HEADERS = ['authorization', 'cookie', 'x-api-key'];
            const redacted = {};
            for (const [key, value] of Object.entries(_options.headers)) {
                redacted[key] = SENSITIVE_HEADERS.includes(key.toLowerCase()) ? '<omitted>' : value;
            }
            _options.headers = redacted;
        }
        if (_options && typeof _options.path === 'string') {
            _options.path = redactSensitiveQueryParams(_options.path);
        }
        error.request = {
            _options,
            _ended,
            _ending,
            _redirectCount,
            _currentUrl: typeof _currentUrl === 'string' ? redactSensitiveQueryParams(_currentUrl) : _currentUrl,
        };
    }
    if ('response' in error) {
        const response = error.response;
        const { request, config } = response;
        // remove request and config
        if (request && 'request' in response) {
            error.response = { ...error.response, request: '<omitted>' };
        }
        if (config && 'config' in response) {
            error.response = { ...error.response, config: '<omitted>' };
        }
    }
    try {
        JSON.stringify(error);
    }
    catch (err) {
        // if we didn't catch a circular reference, serialize the entire object
        return util.inspect(error, false, 4);
    }
    return error;
}
export async function parseAxiosError(response) {
    const responseOrError = await response.catch((err) => err);
    if (responseOrError instanceof Error) {
        const error = cleanError({ ...responseOrError });
        return new LinkedError('request error', undefined, error);
    }
    if (responseOrError.status < 200 || responseOrError.status > 299) {
        return new LinkedError('request status error', undefined, {
            url: responseOrError.config.url,
            method: responseOrError.config.method,
            responseStatus: responseOrError.status,
            responseData: responseOrError.data,
        });
    }
    return responseOrError;
}
/*
  Note: with the following error handling improvement introduced in axios v1.0
  we might be able to simplify or remove these functions:
  https://github.com/axios/axios/pull/4624
*/
export async function handleAxiosError(response, options) {
    const responseOrError = await parseAxiosError(response);
    if (responseOrError instanceof LinkedError) {
        if (!options?.silent) {
            const logger = await getLogger();
            logger.debug(responseOrError, 'request failed');
        }
        return responseOrError;
    }
    return responseOrError.data;
}
/**
 * Given an axios response, throws an error in case the status is not in 200-299 range.
 * The error preserves stack information and deletes the headers & posted data.
 *
 * Assumes that response was created with the { validateStatus: null } option set.
 *
 * @param response the axios response.
 * @returns the response data.
 */
export async function throwAxiosError(response, options) {
    const responseOrError = await handleAxiosError(response, options);
    if (responseOrError instanceof LinkedError) {
        throw responseOrError;
    }
    return responseOrError;
}
/**
 * Given an axios response, logs an error in case the status is not in 200-299, and returns undefined.
 * The logged error preserves stack information and deletes the headers & posted data.
 *
 * Assumes that response was created with the { validateStatus: null } option set.
 *
 * @param response the axios response.
 * @param errorMessage the message to log along with the exception.
 * @param options optional settings. If silent is true, no logging is performed.
 * @param enrichErrorData optional additional data to enrich the error.
 * @returns the response data, or undefined in case of error.
 */
export async function checkAxiosResponse(response, options = {
    errorMessage: 'request error',
    silent: false,
    enrichErrorData: {},
}) {
    const responseOrError = await handleAxiosError(response, { silent: true });
    if (responseOrError instanceof LinkedError) {
        if (!options?.silent) {
            const logger = await getLogger();
            logger.error({ ...responseOrError, ...options.enrichErrorData }, options.errorMessage);
        }
        return undefined;
    }
    return responseOrError;
}
export async function extractValidationError(response) {
    const responseOrError = await handleAxiosError(response);
    if (responseOrError instanceof LinkedError && responseOrError.cause.responseStatus === 400) {
        const responseData = responseOrError.cause.responseData;
        const validationError = Array.isArray(responseData)
            ? responseData
                .map((e) => e.message)
                .filter((e) => Boolean(e))
                .join('\n')
            : responseData?.error;
        return { ok: false, error: validationError || 'Unknown error' };
    }
    if (responseOrError instanceof LinkedError) {
        const logger = await getLogger();
        logger.error(responseOrError);
        return undefined;
    }
    return { ...responseOrError, ok: true };
}
//# sourceMappingURL=axios-errors.js.map